import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { ShieldAlert } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { OverviewTab } from '@/components/custom/chronograph/overview-tab'
import { QueueMonitorTab } from '@/components/custom/chronograph/queue-monitor-tab'
import { RunDetailSheet } from '@/components/custom/chronograph/run-detail-sheet'
import { RunHistoryTab } from '@/components/custom/chronograph/run-history-tab'
import { SyncActions } from '@/components/custom/chronograph/sync-actions'
import { isSameUtcDay } from '@/components/custom/chronograph/shared'
import {
  useDashboardStats,
  useSyncHistory,
  useSyncStatus,
} from '@/hooks/use-chronograph'
import { selectIsInternal, useUserStore } from '@/stores/user.store'

export const Route = createFileRoute('/_authenticated/admin/chronograph')({
  component: ChronographDashboardPage,
})

function ChronographDashboardPage() {
  const isInternalUser = useUserStore(selectIsInternal)
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)

  const dashboard = useDashboardStats()
  const syncStatus = useSyncStatus()
  const history = useSyncHistory({ limit: 50 })

  const dashboardData = dashboard.data
  const syncStatusData = syncStatus.data

  const todayKey = new Date().toISOString().slice(0, 10)
  const todayOutcome = useMemo(() => {
    return (history.data?.data ?? []).reduce(
      (totals, run) => {
        if (
          run.status !== 'COMPLETED' ||
          !isSameUtcDay(run.startedAt, todayKey)
        ) {
          return totals
        }
        totals.created += run.created
        totals.updated += run.updated
        return totals
      },
      { created: 0, updated: 0 }
    )
  }, [history.data?.data, todayKey])

  const overviewLoading = dashboard.isLoading || syncStatus.isLoading
  const overviewError = dashboard.isError || syncStatus.isError

  if (!isInternalUser) {
    return (
      <div className="flex min-h-[420px] items-center justify-center">
        <Card className="w-full max-w-xl border-primary/20 shadow-none">
          <CardHeader>
            <div className="mb-2 inline-flex size-9 items-center justify-center rounded-md bg-destructive/[0.05] text-destructive">
              <ShieldAlert className="size-4" />
            </div>
            <CardTitle>Access Restricted</CardTitle>
            <CardDescription>
              This page is only available to internal admin users.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-5 overflow-y-auto pb-6 pr-4 md:pr-8">
        <PageHeader title="Chronograph Sync" useSeparator={false} />

        <SyncActions onSyncTriggered={() => setActiveTab('queue')} />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center gap-3">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="queue">Queue Monitor</TabsTrigger>
              <TabsTrigger value="history">Run History</TabsTrigger>
            </TabsList>
            {syncStatusData ? (
              <Badge variant={syncStatusData.isEnabled ? 'green' : 'outline'}>
                Sync {syncStatusData.isEnabled ? 'enabled' : 'disabled'}
              </Badge>
            ) : null}
          </div>

          <TabsContent value="overview">
            <OverviewTab
              dashboardData={dashboardData}
              syncStatusData={syncStatusData}
              todayOutcome={todayOutcome}
              isLoading={overviewLoading}
              isError={overviewError}
              onRetry={() => {
                dashboard.refetch()
                syncStatus.refetch()
              }}
            />
          </TabsContent>

          <TabsContent value="queue">
            <QueueMonitorTab
              queueHealth={syncStatusData?.queue}
              recentJobs={syncStatusData?.recentJobs ?? []}
              isStatusLoading={syncStatus.isLoading}
            />
          </TabsContent>

          <TabsContent value="history">
            <RunHistoryTab onSelectRun={setSelectedRunId} />
          </TabsContent>
        </Tabs>
      </div>

      <RunDetailSheet
        selectedRunId={selectedRunId}
        onClose={() => setSelectedRunId(null)}
      />
    </>
  )
}
